import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { setVocalRange } from '../../../../../common/src/learn_rules.ts';
import { useLearnProgress } from '../../../data/use_learn_progress.tsx';
import { OuterPageContent } from '../../../util/shared.tsx';
import { useCurrentDate } from '../../../util/use_current_date.tsx';
import { Header } from '../../header.tsx';
import { getLearnAudioSession, LearnAudioSession } from '../audio/learn_audio_session.ts';
import { micFailureOf, MicPermissionState, queryMicPermission } from '../audio/learn_mic_capture.ts';
import { nowOn } from '../learn_dates.ts';
import { useLearnRangeFinderWaysOut } from '../learn_navigation.tsx';
import { RangeDirectionScreen } from './range_direction_screen.tsx';
import { RangeFailureScreen } from './range_failure_screen.tsx';
import {
  initialRangeFinderState,
  progressSegment,
  RangeFinderState,
  rangeFinderTunables,
  reduceRangeFinder,
} from './range_finder_session.ts';
import { RangePermissionScreen } from './range_permission_screen.tsx';
import { RangeResultScreen } from './range_result_screen.tsx';
import { RangeProgressSegments } from './range_screen_parts.tsx';

// The Range Finder (issues #3481, #3510): the page behind the hub's "Let's find your range!", the
// Pitch card and Pitch Match tile while no range is saved, and the profile drawer's "My vocal
// range". One screen per phase of the state machine in range_finder_session.ts - permission,
// listen (lower), step (lower), done (lower), listen (higher), step (higher), done (higher),
// result, and failed for a microphone that cannot be opened - under the three wordless progress
// segments the page mounts once for every phase. The direction screen serves the listening,
// stepping and done phases of both directions. No footer.
//
// The way out. The Range Finder belongs to no class, so the header's back button and the result's
// and failure screen's button return to wherever the singer came from - the hub, the Pitch class
// screen, or the tab the profile drawer was opened on - by stepping back to it, and say so ("Back
// to Learn", "Back to Pitch", "Back"); with no origin known (a direct link, a new tab) they lead to
// the hub in this entry's place. The result's primary button leads on: to the Pitch class screen
// ("Continue to Pitch"), or into Pitch Match ("Start Pitch Match") for a singer who came from that
// class screen. It takes this entry's place too, so the finished Range Finder is not left behind it
// in the history (learnRangeResultNext in learn_navigation.tsx).
//
// Audio. The Continue tap on the permission screen is the user gesture: it opens the mic on the
// shared Learn audio session (which resumes the one long-lived context) and the session's frames
// drive the state machine from then on. The guide is the piano soundfont: each new guide request
// in the state aims the known-target estimator at the note, schedules the note one lead ahead and,
// once it is scheduled (a soundfont still loading delays it), tells the machine when it sounded,
// so frames while it sounds are never scored; a step that ends cancels whatever the guide still had
// pending, so the listen phase that follows hears the singer rather than the piano. Reaching the
// result saves the range to the member's progress once and then leaves the session (the detector
// unaimed, the guide silenced, the mic ended, the context rested); leaving the page does the same
// without saving. The context is never closed.
//
// Permission. The permission screen is only for a microphone the browser has not already granted.
// On mount the page asks the Permissions API about the microphone: while that answer is pending the
// page renders the header and the segments over a blank body (the query takes milliseconds, and a
// permission screen that appeared only to vanish would be worse than a moment of nothing), and if
// it has not answered within micPermissionQueryMs the screen goes up anyway. A 'granted' answer
// opens the mic there and then, on the transient activation left by the tap that arrived here from
// the hub, and the run starts on the listening screen. Platforms differ: Chrome and Edge remember
// the grant per origin, so a singer who has done this before skips the screen; Firefox and Safari
// answer 'prompt' for the microphone whatever they remember (Firefox may refuse the query outright),
// so they see the screen; and the iOS and Android app wrappers show it or not according to what
// their native permission handling has already granted the webview. Opening the mic without a
// gesture is the case the timeout is for: Chrome and iOS Safari leave AudioContext.resume() pending
// rather than rejecting it, so an openMic that has not resolved within micSkipOpenMs is taken for a
// mic that wants a gesture, and the permission screen goes up - whose Continue tap calls openMic
// again, joins the same in-flight open and resumes the context inside the gesture, finishing what
// the first attempt started. That first open is still listened to behind the screen, because it may
// only have been slow: if it succeeds the microphone is live and the run starts on it, Continue
// untapped, and if it fails the failure screen says so rather than a Continue standing in front of
// a microphone that cannot be opened.
//
// Time. Every transition comes from the frames' audio-clock times, never from timers, so a story
// that injects the fake session and emits frames drives the page through the same transitions a
// real microphone would. `initialState` lets a story open on any phase; the real page always
// starts on the permission screen, because its tap is the gesture that opens the mic.

export const rangeFinderPageId = 'range-finder-page';

export const guideLeadMs = 100; // from a guide request to the note, so the scheduled time is never already past

export const micPermissionQueryMs = 300; // the longest the body stays blank waiting for the browser's answer
export const micSkipOpenMs = 1500; // an openMic still pending after this is waiting for a gesture

// Whether the permission screen is still to be decided on (a blank body), is the way in, or has
// been skipped because the browser had already granted the microphone
type PermissionCheck = 'checking' | 'show' | 'skipped';

interface Props {
  audio?: LearnAudioSession;
  initialState?: RangeFinderState;
  // Injected by stories and tests, which never touch navigator.permissions
  queryMicPermission?: () => Promise<MicPermissionState>;
  // How many times faster than life the stairs scene's choreography runs. A story that plays a
  // whole run has to sit through both directions' endings, which are seconds of animation each.
  sceneSpeed?: number;
}

export const RangeFinderPage = (props: Props) => {
  const audio = props.audio ?? getLearnAudioSession();
  const { backLabel, onBack, nextLabel, onNext } = useLearnRangeFinderWaysOut();
  const { updateProgress, flush } = useLearnProgress();
  const today = useCurrentDate();
  const [state, dispatch] = useReducer(
    reduceRangeFinder,
    props.initialState,
    (initial) => initial ?? initialRangeFinderState(),
  );
  const mountedRef = useRef(true);
  const handledRequestRef = useRef<number | undefined>(undefined);
  const savedRef = useRef(false);
  const startPhaseRef = useRef(state.phase); // a story can open on any phase; only a run that starts at the beginning asks
  const [permissionCheck, setPermissionCheck] = useState<PermissionCheck>('checking');
  const queryPermission = props.queryMicPermission ?? queryMicPermission;

  // Every analysed frame goes to the state machine for the page's life
  useEffect(() => audio.onFrame((frame) => dispatch({ type: 'frame', frame })), [audio]);

  // Does the singer have to be asked at all? See "Permission" in the header: the browser's answer,
  // and then the mic itself, decide between the permission screen and going straight to listening.
  useEffect(() => {
    if (startPhaseRef.current !== 'permission') {
      return;
    }
    let settled = false; // the screen is up, the run has started, or the page has gone
    let openTimer: ReturnType<typeof setTimeout> | undefined;
    const showScreen = () => {
      if (settled || !mountedRef.current) {
        return;
      }
      settled = true;
      clearTimeout(screenTimer);
      clearTimeout(openTimer);
      setPermissionCheck('show');
    };
    const screenTimer = setTimeout(showScreen, micPermissionQueryMs);
    void (async () => {
      const permission = await queryPermission();
      if (settled || !mountedRef.current) {
        return;
      }
      if (permission !== 'granted') {
        showScreen();
        return;
      }
      clearTimeout(screenTimer); // the body stays blank while the mic opens; the race below ends that
      // Exactly what the Continue tap does, on the activation the tap that opened this page left
      const opening = audio.openMic().then(
        () => ({ opened: true }) as const,
        (error: unknown) => ({ opened: false, failure: micFailureOf(error) }) as const,
      );
      const outcome = await Promise.race([
        opening,
        new Promise<undefined>((resolve) => {
          openTimer = setTimeout(() => resolve(undefined), micSkipOpenMs);
        }),
      ]);
      if (settled || !mountedRef.current) {
        return;
      }
      if (!outcome) {
        showScreen(); // the open is waiting for a gesture; the Continue tap finishes this same open
        // The screen is up, but the open the page started is still out there: it may be a slow
        // device rather than a browser waiting for a gesture. If it lands the microphone is live,
        // so the run starts without the Continue tap ever being needed; if it fails, say so rather
        // than leave a Continue standing in front of a microphone that cannot be opened.
        void opening.then((late) => {
          if (!mountedRef.current) {
            return;
          }
          dispatch(late.opened ? { type: 'micOpened' } : { type: 'micFailed', failure: late.failure });
        });
        return;
      }
      settled = true;
      clearTimeout(openTimer);
      dispatch(outcome.opened ? { type: 'micOpened' } : { type: 'micFailed', failure: outcome.failure });
      setPermissionCheck('skipped');
    })();
    return () => {
      settled = true;
      clearTimeout(screenTimer);
      clearTimeout(openTimer);
    };
  }, [audio, queryPermission]);

  // Leaving the page unaims the detector, silences anything pending, ends the mic track and
  // suspends the context - all of it inside the session's own leave(), which guards each step of
  // the microphone teardown so that a guide note is never left sounding on the hub behind the singer
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      audio.leave();
    };
  }, [audio]);

  // The guide follows the state: each new request aims the detector, plays the note and reports
  // when it sounded. The ref keeps StrictMode's doubled effects from playing a note twice.
  const requestId = state.phase === 'step' ? state.guide.requestId : undefined;
  const guideMidi = state.phase === 'step' ? state.guide.midi : undefined;
  useEffect(() => {
    if (requestId === undefined || guideMidi === undefined) {
      // The step ended (a note recorded at the bound, a note the singer cannot reach, the stop
      // button): silence the guide as well as unaiming the detector, so a note still to sound - or
      // one whose soundfont is still loading - never plays into the listen phase that follows and
      // gets heard as the singer
      handledRequestRef.current = undefined;
      audio.setTargetMidi(undefined);
      audio.cancelScheduled();
      return;
    }
    if (handledRequestRef.current === requestId) {
      return;
    }
    handledRequestRef.current = requestId;
    audio.setTargetMidi(guideMidi);
    const atMs = audio.nowMs() + guideLeadMs;
    void audio.schedulePianoNote(guideMidi, atMs, rangeFinderTunables.guideMs).then(() => {
      if (!mountedRef.current) {
        return;
      }
      dispatch({ type: 'guideStarted', requestId, atMs: Math.max(atMs, audio.nowMs()) });
    });
  }, [audio, requestId, guideMidi]);

  // The result saves the range once (redoing overwrites), then the mic and the context rest
  useEffect(() => {
    if (state.phase !== 'result' || savedRef.current) {
      return;
    }
    savedRef.current = true;
    const { range } = state;
    updateProgress((current) => setVocalRange(current, range, nowOn(today)));
    void flush();
    audio.leave(); // the run is over: the same teardown as leaving the page
  }, [audio, flush, state, today, updateProgress]);

  // The Continue tap is the gesture that opens the mic (and resumes the context)
  const onContinue = useCallback(async () => {
    try {
      await audio.openMic();
    } catch (error) {
      if (!mountedRef.current) {
        return;
      }
      dispatch({ type: 'micFailed', failure: micFailureOf(error) });
      return;
    }
    if (mountedRef.current) {
      dispatch({ type: 'micOpened' });
    }
  }, [audio]);

  // A direction that has ended waits on the scene (issue #3510, addendum 3): the singer hops onto
  // the landing, the bubble congratulates him and fades, and the descent hands over to the ascent
  // and announces it. The direction screen calls this when that sequence has run, and only then
  // does the machine move on - from the descent's done to the ascent's listen phase, from the
  // ascent's to the result.
  const onDone = useCallback(() => dispatch({ type: 'continueAfterDone' }), []);

  const onReplay = useCallback(() => dispatch({ type: 'replay' }), []);
  const onStop = useCallback(() => dispatch({ type: 'stopDirection' }), []);

  return (
    <OuterPageContent id={rangeFinderPageId} data-phase={state.phase}>
      <Header title="Find your range" onBack={() => void onBack()} />
      <RangeProgressSegments segment={progressSegment(state)} />
      {state.phase === 'permission' && permissionCheck === 'show' && (
        <RangePermissionScreen onContinue={() => void onContinue()} />
      )}
      {(state.phase === 'listen' || state.phase === 'step' || state.phase === 'done') && (
        <RangeDirectionScreen
          state={state}
          onReplay={onReplay}
          onStop={onStop}
          onDone={onDone}
          sceneSpeed={props.sceneSpeed}
        />
      )}
      {state.phase === 'result' && (
        <RangeResultScreen
          range={state.range}
          part={state.part}
          nextLabel={nextLabel}
          onNext={() => void onNext()}
          backLabel={backLabel}
          onBack={() => void onBack()}
        />
      )}
      {state.phase === 'failed' && (
        <RangeFailureScreen failure={state.failure} backLabel={backLabel} onBack={() => void onBack()} />
      )}
    </OuterPageContent>
  );
};
